import React, { useState, useEffect } from 'react';
import { Plus, Award, Download } from 'lucide-react';
import { certificateService } from '../../../lib/services.ipd';
import type { BirthDeathCertificate } from '../../../lib/services.ipd';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { generateBirthCertificatePDF, generateDeathCertificatePDF } from '../../../lib/pdf';

interface CertificatesTabProps {
  admissionId: string;
}

export default function CertificatesTab({ admissionId }: CertificatesTabProps) {
  const [certificates, setCertificates] = useState<BirthDeathCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCertificateForm, setShowCertificateForm] = useState(false);
  const [certificateType, setCertificateType] = useState<'birth' | 'death'>('birth');
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    loadData();
  }, [admissionId]);

  const loadData = async () => {
    try {
      const data = await certificateService.getByAdmission(admissionId);
      setCertificates(data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      const certificate = await certificateService.create({
        admission_id: admissionId,
        certificate_type: certificateType,
        patient_name: data.patient_name,
        details: data,
        issued_at: new Date().toISOString(),
      });

      const pdfData = certificateType === 'birth' ? {
        certificate_no: certificate.certificate_no,
        patient_name: data.patient_name,
        date_of_birth: data.date_of_birth,
        time_of_birth: data.time_of_birth,
        weight: data.weight,
        apgar_score: data.apgar_score,
        mother_name: data.mother_name,
        father_name: data.father_name,
        hospital_name: 'Hospital Name',
        issued_date: certificate.issued_at,
      } : {
        certificate_no: certificate.certificate_no,
        patient_name: data.patient_name,
        date_of_death: data.date_of_death,
        time_of_death: data.time_of_death,
        cause_of_death: data.cause_of_death,
        age: data.age,
        hospital_name: 'Hospital Name',
        issued_date: certificate.issued_at,
      };

      const doc = certificateType === 'birth' 
        ? generateBirthCertificatePDF(pdfData as any)
        : generateDeathCertificatePDF(pdfData as any);
      
      const pdfBlob = doc.output('blob');
      await certificateService.uploadPDF(certificate.id, pdfBlob);

      toast.success('Certificate created successfully');
      reset();
      setShowCertificateForm(false);
      loadData();
    } catch (error) {
      console.error('Failed to create certificate:', error);
      toast.error('Failed to create certificate');
    }
  };

  const downloadCertificate = async (cert: BirthDeathCertificate) => {
    if (!cert.pdf_url) {
      toast.error('PDF not available');
      return;
    }

    try {
      const url = await certificateService.getSignedUrl(cert.pdf_url);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to download certificate:', error);
      toast.error('Failed to download certificate');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Birth/Death Certificates</h3>
        <button
          onClick={() => {
            setShowCertificateForm(!showCertificateForm);
            reset();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          New Certificate
        </button>
      </div>

      {showCertificateForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Certificate Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="birth"
                  checked={certificateType === 'birth'}
                  onChange={(e) => setCertificateType('birth')}
                  className="mr-2"
                />
                Birth Certificate
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="death"
                  checked={certificateType === 'death'}
                  onChange={(e) => setCertificateType('death')}
                  className="mr-2"
                />
                Death Certificate
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {certificateType === 'birth' ? 'Child' : 'Deceased'} Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('patient_name', { required: 'Name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {errors.patient_name && <p className="text-sm text-red-600 mt-1">{errors.patient_name.message as string}</p>}
          </div>

          {certificateType === 'birth' ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                  <input
                    type="date"
                    {...register('date_of_birth')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time of Birth</label>
                  <input
                    type="time"
                    {...register('time_of_birth')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                  <input
                    type="text"
                    {...register('weight')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">APGAR Score</label>
                  <input
                    type="text"
                    {...register('apgar_score')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Name</label>
                  <input
                    type="text"
                    {...register('mother_name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Father's Name</label>
                  <input
                    type="text"
                    {...register('father_name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Death</label>
                  <input
                    type="date"
                    {...register('date_of_death')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time of Death</label>
                  <input
                    type="time"
                    {...register('time_of_death')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                <input
                  type="text"
                  {...register('age')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cause of Death</label>
                <textarea
                  {...register('cause_of_death')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Generate Certificate
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCertificateForm(false);
                reset();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {certificates.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No certificates recorded</p>
        ) : (
          certificates.map((cert) => (
            <div key={cert.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Award className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-gray-900">
                    {cert.certificate_type === 'birth' ? 'Birth' : 'Death'} Certificate
                  </span>
                </div>
                {cert.pdf_url && (
                  <button
                    onClick={() => downloadCertificate(cert)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-600">Certificate No: {cert.certificate_no}</p>
              <p className="text-sm text-gray-600">Patient: {cert.patient_name}</p>
              <p className="text-sm text-gray-500">Issued: {new Date(cert.issued_at).toLocaleDateString()}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
